// Canonical vocabulary for marking-tool matching.
//
// The template corpus spans three content generations (AU legacy, US seed,
// NZ seed) whose labels never converged: AU WordPress subjects say
// "Full Response" where US/NZ subjects and all tool metadata say
// "Extended Response"; VIC juniors say "Short Response" where everything
// else says "Short Answer". This module maps every label onto a small
// canonical vocabulary so the match engine never has to compare raw
// region-specific strings.

export const RESPONSE_MODES = {
  EXTENDED: 'EXTENDED',
  TARGETED: 'TARGETED',
  SHORT: 'SHORT',
};

export const normalizeLabel = (value) =>
  String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const normalizeCompact = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** Canonical response mode for a response-type OR task-type label. */
export const responseModeOf = (label) => {
  const compact = normalizeCompact(label);
  if (!compact) return null;
  if (compact.includes('shortanswer') || compact.includes('shortresponse')) {
    return RESPONSE_MODES.SHORT;
  }
  if (compact.includes('targetedparagraph')) return RESPONSE_MODES.TARGETED;
  if (
    compact.includes('extendedresponse') ||
    compact.includes('fullresponse') ||
    compact.includes('longanswer')
  ) {
    return RESPONSE_MODES.EXTENDED;
  }
  return null;
};

/**
 * Response mode for a question. A short-answer-like TASK type is decisive:
 * across the whole WP feed such task types carry either no response types
 * (AU) or only short ones (US SAQ, NZ), so a conflicting response type can
 * only be a stale leftover from a task-type switch (the submission-type
 * field hides for short answers, historically leaving e.g. "Full Response"
 * behind in the form state) and must not drag the mode back to extended.
 * Otherwise the response type wins when it maps to a mode, then the task
 * type; a question with a task type but no recognizable response type
 * defaults to EXTENDED (every non-short task type in the WP feed is
 * essay-like).
 */
export const deriveResponseMode = (responseType, taskType) => {
  const fromTask = responseModeOf(taskType);
  if (fromTask === RESPONSE_MODES.SHORT) return RESPONSE_MODES.SHORT;
  const fromResponse = responseModeOf(responseType);
  if (fromResponse) return fromResponse;
  if (fromTask) return fromTask;
  if (String(taskType || '').trim()) return RESPONSE_MODES.EXTENDED;
  return null;
};

// ---------------------------------------------------------------------------
// Subject codes
// ---------------------------------------------------------------------------

const AU_STATES = new Set(['NSW', 'VIC', 'QLD', 'SA', 'TAS', 'ACT', 'NT']);
// WA is deliberately ambiguous (Western Australia / Washington) — mirrors
// Region.java, which counts it as neither AU-only nor US-only.
const AU_ONLY_STATES = AU_STATES;
const US_STATES = new Set([
  'AK',
  'AL',
  'AR',
  'AZ',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'ND',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VT',
  'WI',
  'WV',
  'WY',
]);
const US_ONLY_STATES = new Set(
  [...US_STATES].filter((s) => !AU_STATES.has(s) && s !== 'WA')
);

/** Country implied by a state list: 'AU' | 'US' | 'NZ' | null (universal/mixed). */
export const countryForStates = (states) => {
  const list = (states || []).map((s) => String(s || '').toUpperCase());
  if (list.length === 0) return null;
  if (list.includes('NZ')) return 'NZ';
  const hasUsOnly = list.some((s) => US_ONLY_STATES.has(s));
  const hasAuOnly = list.some((s) => AU_ONLY_STATES.has(s));
  if (hasUsOnly && !hasAuOnly) return 'US';
  if (hasAuOnly && !hasUsOnly) return 'AU';
  return null;
};

// Subject "family" groupings used for closest-match relaxation when a class
// subject has no tools declaring it (e.g. AU-VIC-EAL -> the VCE English
// library). Grouping is by discipline, so an Economics class can fall back
// to Business tools but never to English ones.
const FAMILY_RULES = [
  {
    family: 'english',
    regex:
      /english|eal[db]?$|literature|englit|lit(stud)?$|eng$|eng\d|aplang|aplit|drama/,
  },
  { family: 'business-economics', regex: /business|commerce|econ/ },
  {
    family: 'history',
    regex: /history|modern|ancient|revs|apush|apworld|ush$|wh$|classic|clas/,
  },
  { family: 'health-pe', regex: /health|hhd|pe$|physicaled/ },
  { family: 'legal-civics', regex: /legal|apgov|gov$|civics/ },
  { family: 'geography', regex: /geo/ },
  { family: 'social-studies', regex: /socs|socialstud/ },
  { family: 'media', regex: /media/ },
];

const familyOfName = (name) => {
  const compact = normalizeCompact(name);
  if (!compact) return null;
  const hit = FAMILY_RULES.find((r) => r.regex.test(compact));
  return hit ? hit.family : compact;
};

/**
 * Parse a WP subject code into structured parts:
 *   AU-NSW-English-Adv -> { country: 'AU', state: 'NSW', family: 'english' }
 *   ELA-ENG9           -> { country: 'US', family: 'english', year: 9 }
 *   NZ-SOCS11          -> { country: 'NZ', state: 'NZ', family: 'social-studies', year: 11 }
 *   AllEnglish         -> { country: 'AU', family: 'english', universal: true }
 */
export const parseSubjectCode = (rawCode) => {
  const code = String(rawCode || '').trim();
  const compact = normalizeCompact(code);
  if (!compact) return null;
  const base = {
    code,
    compact,
    country: null,
    state: null,
    family: null,
    year: null,
    universal: false,
  };

  if (compact === 'allenglish') {
    return { ...base, country: 'AU', family: 'english', universal: true };
  }
  let m = code.match(/^AU-([A-Za-z]+)-(.+)$/i);
  if (m) {
    return {
      ...base,
      country: 'AU',
      state: m[1].toUpperCase(),
      family: familyOfName(m[2]),
    };
  }
  m = code.match(/^NZ-([A-Za-z]+)(\d+)?$/i);
  if (m) {
    return {
      ...base,
      country: 'NZ',
      state: 'NZ',
      family: familyOfName(m[1]),
      year: m[2] ? parseInt(m[2], 10) : null,
    };
  }
  m = code.match(/^(ELA|SS)-(.+)$/i);
  if (m) {
    const name = m[2];
    const grade = name.match(/^ENG(\d+)$/i);
    return {
      ...base,
      country: 'US',
      family: m[1].toUpperCase() === 'ELA' ? 'english' : familyOfName(name),
      year: grade ? parseInt(grade[1], 10) : null,
    };
  }
  return { ...base, family: familyOfName(code) };
};

// ---------------------------------------------------------------------------
// Token matching (task type <-> title)
// ---------------------------------------------------------------------------

// Words that appear in nearly every tool title / task type and carry no
// discriminating signal.
const STOPWORDS = new Set([
  'a',
  'an',
  'and',
  'the',
  'of',
  'or',
  'to',
  'for',
  'via',
  'in',
  'on',
  'essay',
  'writing',
  'written',
  'response',
  'question',
  'questions',
  'task',
  'rubric',
  'rubrics',
  'criteria',
  'checklist',
  'comment',
  'bank',
  'marking',
  'template',
  'success',
  'statement',
  'aligned',
  'standards',
  'grade',
  'grades',
  'year',
  'years',
  'english',
]);

export const tokenizeForMatch = (text) =>
  String(text || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3 && !STOPWORDS.has(t));

// Two tokens "match" when equal or sharing a >=5-char prefix — catches
// argument/argumentative, inform(ative|ational), analysis/analytical,
// history/historical without a real stemmer.
const tokensMatch = (a, b) => {
  if (a === b) return true;
  const len = Math.min(a.length, b.length);
  if (len < 5) return false;
  return a.slice(0, 5) === b.slice(0, 5);
};

/**
 * How strongly a tool title (or declared label) reflects a task-type label:
 * fraction of the task type's meaningful tokens found in the text (0..1).
 * This is the only way to tell the 27 metadata-less US ELA rubrics apart
 * ("Argumentative Essay Rubric" vs "Narrative" vs "Informative/Explanatory").
 */
export const taskTypeAffinity = (text, taskType) => {
  const wanted = tokenizeForMatch(taskType);
  if (wanted.length === 0) return 0;
  const have = tokenizeForMatch(text);
  if (have.length === 0) return 0;
  const matched = wanted.filter((w) => have.some((h) => tokensMatch(h, w)));
  return matched.length / wanted.length;
};

// ---------------------------------------------------------------------------
// Title conventions: marks, stimulus, compare, sections
// ---------------------------------------------------------------------------

/**
 * All mark specs in a title as {min,max} ranges.
 *   "5 Mark Question"    -> [{min:5,max:5}]
 *   "3–8 Mark Question"  -> [{min:3,max:8}]
 *   "Part B (15 marks)"  -> [{min:15,max:15}]
 */
export const parseTitleMarkRanges = (title) => {
  const ranges = [];
  const regex = /(\d+)\s*(?:(?:[-–—~]|to)\s*(\d+))?\s*[- ]?marks?\b/gi;
  let match;
  while ((match = regex.exec(String(title || ''))) !== null) {
    const a = Number(match[1]);
    const b = match[2] != null ? Number(match[2]) : a;
    ranges.push({ min: Math.min(a, b), max: Math.max(a, b) });
  }
  return ranges;
};

export const titleMatchesMarks = (title, marks) =>
  marks != null &&
  parseTitleMarkRanges(title).some((r) => marks >= r.min && marks <= r.max);

/** 'with' | 'without' | null. A bare "Stimulus" mention counts as 'with'. */
export const stimulusVariantOf = (title) => {
  const t = String(title || '');
  if (/\bwithout\s+stimulus|\bno\s+stimulus|\bstimulus[- ]free/i.test(t)) {
    return 'without';
  }
  if (/stimulus/i.test(t)) return 'with';
  return null;
};

export const isCompareTitle = (title) => /\bcompar/i.test(String(title || ''));

/** True when the question itself asks for a comparison. */
export const compareSignalOf = (taskType, questionText) =>
  /\bcompar|connections/i.test(String(taskType || '')) ||
  /\bcompar|\bcontrast/i.test(String(questionText || ''));

// Success-criteria structure. Titles encode the section either as the whole
// title (AU: "Introduction", "Body Paragraph (Single)") or as a suffix
// (US: "Argumentative Essay — Introduction & Thesis (Claim)").
const SECTION_RULES = [
  { section: 'intro', regex: /\bintro(?:duction)?\b|\bexecutive\s+summary\b/i },
  { section: 'conclusion', regex: /\bconclu(?:sion|ding)/i },
  { section: 'body', regex: /\bbody\b|\bdevelopment\b/i },
];

/** 'intro' | 'body' | 'conclusion' | null */
export const sectionOf = (title) => {
  const t = String(title || '');
  const hit = SECTION_RULES.find((r) => r.regex.test(t));
  return hit ? hit.section : null;
};

/** 'single' | 'multiple' | null — body-paragraph-count variant marker. */
export const bodyVariantOf = (title) => {
  const t = String(title || '');
  if (/\bsingle\b|\bsingle\s+paragraph\b/i.test(t)) return 'single';
  if (/\bmultiple\b/i.test(t)) return 'multiple';
  return null;
};

/** 'full' | 'targeted' | null — response-mode variant marker in a title. */
export const responseVariantOf = (title) => {
  const t = String(title || '');
  if (/\(full\)|\bfull\s+response\b/i.test(t)) return 'full';
  if (/\(single\s+paragraph\)|\btargeted\b/i.test(t)) return 'targeted';
  return null;
};

/**
 * A key identifying which "set" a success criteria belongs to, so section
 * picks stay coherent (never an AP-Gov introduction with an AU conclusion).
 * US/NZ titles carry the set name before the em-dash; AU section titles are
 * bare, so the set identity comes from the metadata signature.
 */
export const successCriteriaSetKey = (tool) => {
  const title = String(tool?.title || '');
  const dash = title.split(/\s+—\s+/);
  let prefix = dash.length > 1 ? dash[0] : '';
  if (!prefix) {
    // Strip section words and variant suffixes; whatever remains names the set.
    prefix = title
      .replace(/[-–—:]?\s*(stimulus\s*question|section\s+[ivx0-9]+)\s*$/i, '')
      .replace(
        /\bbody\s+paragraphs?\b|\bintro(?:duction)?\b|\bconclu\w+\b/gi,
        ''
      )
      .replace(/\((single|multiple)\)/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const md = tool?.metadata || {};
  const signature = [
    (md.subjects || [])
      .map((s) => normalizeCompact(s?.subjectCode ?? s))
      .sort()
      .join(','),
    (md.taskTypes || []).map(normalizeCompact).sort().join(','),
    (md.years || []).map(String).sort().join(','),
  ].join('|');
  return `${normalizeCompact(prefix)}|${signature}`;
};

export const getYearNum = (y) => {
  if (y == null) return null;
  const num = parseInt(String(y).replace(/\D/g, ''), 10);
  return Number.isNaN(num) ? null : num;
};
